/* eslint-disable react-hooks/exhaustive-deps */
import { withRouter } from "../_shared/withRouter";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import { globalAlert, globalLoader } from "../actions/commonActions";
import { logOut } from "../_shared/commonFunction";
import { learnerPortalUrl, LEARNER_DOMAIN, learnerPath } from "../lms/_learnerConfig";
import {
  lmsGetSettingsApi,
  lmsUpdateSettingsApi,
  lmsListRegionsApi,
  lmsSaveRegionApi,
  lmsListDistrictsApi,
  lmsSaveDistrictApi,
  lmsDeleteDistrictApi,
} from "./_lmsActions";

const listOf = (data, key) =>
  Array.isArray(data) ? data : data?.[key] || data?.list || [];

// Normalise an approved-domains value (array OR comma string) → clean array:
// lowercase + trim + de-dupe.
const normalizeDomains = (val) => {
  const raw = Array.isArray(val) ? val : String(val || "").split(",");
  const out = [];
  raw
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean)
    .forEach((d) => {
      if (!out.includes(d)) out.push(d);
    });
  return out;
};

const regionCodeFromName = (name = "") =>
  String(name || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 30);

const createSupportState = (support = {}) => ({
  email: String(support?.email || "").trim(),
  contactNumber: String(support?.contactNumber || "").trim(),
  helpAndSupport: String(support?.helpAndSupport || "").trim(),
  notes: String(support?.notes || "").trim(),
});

const SETTINGS_TABS = [
  { key: "access", label: "Access & approvals", icon: "fa-shield-alt" },
  { key: "region", label: "Regions & districts", icon: "fa-map-marked-alt" },
  { key: "support", label: "Help & support", icon: "fa-life-ring" },
  { key: "portal", label: "Learner URL", icon: "fa-link" },
];

const settingsTabFromSearch = (search = "") => {
  const tab = new URLSearchParams(search).get("tab") || "access";
  if (tab === "districts") return "region";
  return SETTINGS_TABS.some((item) => item.key === tab) ? tab : "access";
};

// Admin → ESHS Learning → Settings. Manages the org's Approved Email Domains
// (learners on these domains are auto-approved; others go to admin review).
// This moved here from the super-admin org form — it's the org admin's control.
const LmsSettings = (props) => {
  const { t } = useTranslation();
  const [navbarToggle, setNavbarToggle] = useState(false);
  const [allowView, setAllowView] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    settingsTabFromSearch(props?.location?.search)
  );
  const [domains, setDomains] = useState([]);
  const [initial, setInitial] = useState([]);
  const [domainInput, setDomainInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [supportForm, setSupportForm] = useState(() => createSupportState());
  const [supportInitial, setSupportInitial] = useState(() => createSupportState());
  const [savingSupport, setSavingSupport] = useState(false);

  // Learner portal URL tab — the tenant path is `/e-learning/{lmsShortName}`.
  // The short name is set by the platform team (super-admin) and is read-only
  // here; GET /settings returns it, but POST /settings does NOT accept it.
  const [shortName, setShortName] = useState("");
  const [lmsEnabled, setLmsEnabled] = useState(true);
  const [copiedKey, setCopiedKey] = useState("");

  // Default Region — moved here from the super-admin org form. The admin picks
  // it from THIS org's configured Regions (loaded below), so it's inherently
  // validated against them. Stored on Organization.lmsDefaultRegion (by name).
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newRegion, setNewRegion] = useState({ name: "" });
  const [savingRegionConfig, setSavingRegionConfig] = useState(false);
  const [editingRegionId, setEditingRegionId] = useState("");
  const [editingRegion, setEditingRegion] = useState({ name: "" });
  const [newDistrict, setNewDistrict] = useState({ name: "" });
  const [addingDistrict, setAddingDistrict] = useState(false);
  const [editingDistrictId, setEditingDistrictId] = useState("");
  const [editingDistrict, setEditingDistrict] = useState({ regionId: "", name: "" });
  // "View districts" modal — the region whose districts are being managed.
  const [districtModalRegion, setDistrictModalRegion] = useState(null);
  const [confirmDeleteDistrict, setConfirmDeleteDistrict] = useState(null);

  useEffect(() => {
    callBasicApi();
    loadRegions();
    loadDistricts();
    if (new URLSearchParams(props?.location?.search).get("tab") === "districts") {
      props?.history?.replace({
        pathname: "/admin/lms/settings",
        search: "?tab=region",
      });
    }
  }, []);

  const loadRegions = async () => {
    try {
      const res = await lmsListRegionsApi(true);
      if (res?.data?.status === 200) {
        const list = listOf(res?.data?.data, "regions");
        setRegions(list);
        setSelectedRegionId((current) => current || list.find((r) => r?.isEnabled !== false)?._id || list[0]?._id || "");
      }
    } catch (e) {
      /* surfaced by the client */
    }
  };

  const loadDistricts = async () => {
    try {
      const res = await lmsListDistrictsApi({ includeDisabled: true });
      if (res?.data?.status === 200)
        setDistricts(listOf(res?.data?.data, "districts"));
      else globalAlert("error", res?.data?.message);
    } catch (e) {
      /* surfaced by the client */
    }
  };

  const addDistrict = async () => {
    if (!selectedRegionId) {
      globalAlert("warning", t("admin.lms.settings.districtRegionRequired", "Select a region from the list first."));
      return;
    }
    if (!newDistrict.name.trim()) {
      globalAlert("warning", t("admin.lms.settings.districtNameRequired", "District name is required."));
      return;
    }
    setAddingDistrict(true);
    try {
      const res = await lmsSaveDistrictApi({
        regionId: selectedRegionId,
        name: newDistrict.name.trim(),
      });
      const { status, message } = res?.data || {};
      if (status === 200) {
        globalAlert("success", message || t("admin.lms.settings.districtAdded", "District added."));
        setNewDistrict({ name: "" });
        loadDistricts();
      } else {
        globalAlert("warning", message);
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setAddingDistrict(false);
  };

  const callBasicApi = async () => {
    try {
      globalLoader(true);
      const res = await lmsGetSettingsApi();
      if (res?.data?.status === 200) {
        const s = res?.data?.data?.settings || res?.data?.data || {};
        const d = normalizeDomains(s.lmsAutoApproveDomains);
        setDomains(d);
        setInitial(d);
        setShortName(s.lmsShortName || "");
        const nextSupport = createSupportState(s.supportConfig);
        setSupportForm(nextSupport);
        setSupportInitial(nextSupport);
        if (s.lmsEnabled != null) setLmsEnabled(s.lmsEnabled !== false);
      } else {
        globalAlert("error", res?.data?.message);
      }
      setAllowView(true);
      globalLoader(false);
    } catch (error) {
      // Render the page even if the settings fetch fails (e.g. endpoint not yet
      // live) so the admin isn't bounced; a save will surface the real error.
      setAllowView(true);
      globalLoader(false);
      if (error?.response?.data?.status === 401) {
        logOut(props?.history, "/admin/login");
      }
    }
  };

  const commitDomain = () => {
    let cleanDomain = domainInput.trim();
    if (cleanDomain.startsWith("@")) {
      cleanDomain = cleanDomain.substring(1);
    }
    setDomains((prev) => normalizeDomains([...prev, cleanDomain]));
    setDomainInput("");
  };

  const onDomainKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (domainInput.trim()) commitDomain();
    } else if (e.key === "Backspace" && !domainInput && domains.length) {
      setDomains(domains.slice(0, -1));
    }
  };

  const removeDomain = (d) => setDomains(domains.filter((x) => x !== d));

  const dirty = domains.join(",") !== initial.join(",");
  const supportDirty = JSON.stringify(supportForm) !== JSON.stringify(supportInitial);

  const saveRegionConfig = async () => {
    if (!newRegion.name.trim()) {
      globalAlert("warning", t("admin.lms.settings.regionNameRequired", "Region name is required."));
      return;
    }
    setSavingRegionConfig(true);
    try {
      const res = await lmsSaveRegionApi({
        name: newRegion.name.trim(),
        code: regionCodeFromName(newRegion.name),
      });
      const { status, message } = res?.data || {};
      if (status === 200) {
        globalAlert("success", message || t("admin.lms.settings.regionSaved", "Region saved."));
        setNewRegion({ name: "" });
        await loadRegions();
      } else {
        globalAlert("warning", message);
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message || t("common.somethingWentWrong"));
    }
    setSavingRegionConfig(false);
  };

  const beginEditRegion = (region) => {
    setEditingRegionId(region?._id || "");
    setEditingRegion({ name: region?.name || "" });
  };

  const cancelEditRegion = () => {
    setEditingRegionId("");
    setEditingRegion({ name: "" });
  };

  const updateRegion = async (region) => {
    if (!editingRegion.name.trim()) {
      globalAlert("warning", t("admin.lms.settings.regionNameRequired", "Region name is required."));
      return;
    }
    try {
      globalLoader(true);
      const res = await lmsSaveRegionApi({
        regionId: region?._id,
        name: editingRegion.name.trim(),
        isEnabled: !!region?.isEnabled,
      });
      const { status, message } = res?.data || {};
      if (status === 200) {
        globalAlert("success", message || t("admin.lms.settings.regionSaved", "Region saved."));
        cancelEditRegion();
        await loadRegions();
      } else {
        globalAlert("warning", message);
      }
      globalLoader(false);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message || t("common.somethingWentWrong"));
    }
  };

  const toggleRegion = async (region) => {
    try {
      globalLoader(true);
      const res = await lmsSaveRegionApi({
        regionId: region?._id,
        name: region?.name,
        isEnabled: !region?.isEnabled,
      });
      const { status, message } = res?.data || {};
      if (status === 200) {
        globalAlert(
          "success",
          message ||
          (region?.isEnabled
            ? t("admin.lms.settings.regionDeactivated", "Region deactivated.")
            : t("admin.lms.settings.regionActivated", "Region activated."))
        );
        await loadRegions();
        await loadDistricts();
      } else {
        globalAlert("warning", message);
      }
      globalLoader(false);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message || t("common.somethingWentWrong"));
    }
  };

  const beginEditDistrict = (district) => {
    setEditingDistrictId(district?._id || "");
    setEditingDistrict({
      regionId: district?.region?._id || district?.region || district?.regionId || "",
      name: district?.name || "",
    });
  };

  const cancelEditDistrict = () => {
    setEditingDistrictId("");
    setEditingDistrict({ regionId: "", name: "" });
  };

  const updateDistrict = async (district) => {
    if (!editingDistrict.regionId) {
      globalAlert("warning", t("admin.lms.settings.districtRegionRequired", "Select a region for the district."));
      return;
    }
    if (!editingDistrict.name.trim()) {
      globalAlert("warning", t("admin.lms.settings.districtNameRequired", "District name is required."));
      return;
    }
    try {
      globalLoader(true);
      const res = await lmsSaveDistrictApi({
        districtId: district?._id,
        regionId: editingDistrict.regionId,
        name: editingDistrict.name.trim(),
        isEnabled: !!district?.isEnabled,
      });
      const { status, message } = res?.data || {};
      if (status === 200) {
        globalAlert("success", message || t("admin.lms.settings.districtSaved", "District saved."));
        cancelEditDistrict();
        await loadDistricts();
      } else {
        globalAlert("warning", message);
      }
      globalLoader(false);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message || t("common.somethingWentWrong"));
    }
  };

  const deleteDistrict = async (ok) => {
    const district = confirmDeleteDistrict;
    setConfirmDeleteDistrict(null);
    if (!ok || !district) return;
    try {
      globalLoader(true);
      const res = await lmsDeleteDistrictApi(district._id);
      const { status, message } = res?.data || {};
      if (status === 200) {
        globalAlert("success", message || t("admin.lms.settings.districtDeleted", "District deleted."));
        await loadDistricts();
      } else {
        globalAlert("warning", message);
      }
      globalLoader(false);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message || t("common.somethingWentWrong"));
    }
  };

  const districtRegionId = (d) => d?.region?._id || d?.region || d?.regionId || "";
  const districtsOfRegion = (regionId) =>
    districts.filter((d) => String(districtRegionId(d)) === String(regionId));

  // Open the per-region "Manage districts" modal.
  const openDistrictModal = (region) => {
    setSelectedRegionId(region?._id || "");
    cancelEditDistrict();
    setNewDistrict({ name: "" });
    setDistrictModalRegion(region);
  };
  const closeDistrictModal = () => {
    setDistrictModalRegion(null);
    cancelEditDistrict();
    setNewDistrict({ name: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      globalLoader(true);
      const res = await lmsUpdateSettingsApi({ lmsAutoApproveDomains: domains });
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        const saved = normalizeDomains(
          (data?.settings || data || {}).lmsAutoApproveDomains || domains
        );
        setDomains(saved);
        setInitial(saved);
        globalAlert("success", message || t("admin.lms.settings.saved", "Settings saved."));
      } else if (status === 204) {
        // Soft validation error — surface inline, don't throw.
        globalAlert("warning", message);
      } else {
        globalAlert("error", message);
      }
      globalLoader(false);
    } catch (error) {
      globalAlert("error", error?.response?.data?.message || t("common.somethingWentWrong"));
      globalLoader(false);
      if (error?.response?.data?.status === 401) {
        logOut(props?.history, "/admin/login");
      }
    }
    setSaving(false);
  };

  const saveSupport = async () => {
    setSavingSupport(true);
    try {
      globalLoader(true);
      const payload = { supportConfig: createSupportState(supportForm) };
      const res = await lmsUpdateSettingsApi(payload);
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        const savedSupport = createSupportState((data?.settings || data || {}).supportConfig);
        setSupportForm(savedSupport);
        setSupportInitial(savedSupport);
        globalAlert("success", message || t("admin.lms.settings.saved", "Settings saved."));
      } else if (status === 204) {
        globalAlert("warning", message);
      } else {
        globalAlert("error", message);
      }
      globalLoader(false);
    } catch (error) {
      globalAlert("error", error?.response?.data?.message || t("common.somethingWentWrong"));
      globalLoader(false);
      if (error?.response?.data?.status === 401) {
        logOut(props?.history, "/admin/login");
      }
    }
    setSavingSupport(false);
  };

  const inputRef = useRef(null);

  const selectTab = (tab) => {
    setActiveTab(tab);
    props?.history?.replace({
      pathname: "/admin/lms/settings",
      search: `?tab=${tab}`,
    });
  };

  const isEnabled = (item) => item?.isEnabled !== false;
  const regionRows = regions.filter((region) => {
    if (statusFilter === "active") return isEnabled(region);
    if (statusFilter === "inactive") return !isEnabled(region);
    return true;
  });

  // Districts shown inside the "View districts" modal for the open region.
  const modalRegionDistricts = districtModalRegion
    ? districtsOfRegion(districtModalRegion._id)
    : [];

  const copyToClipboard = async (key, text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? "" : k)), 1800);
    } catch (e) {
      globalAlert("error", t("admin.lms.settings.copyFailed", "Could not copy — please copy it manually."));
    }
  };

  // Learner portal links, built from the tenant short name + LEARNER_DOMAIN.
  const portalLoginUrl = shortName ? learnerPortalUrl(shortName, learnerPath(shortName, "login")) : "";
  const portalRegisterUrl = shortName ? learnerPortalUrl(shortName, learnerPath(shortName, "register")) : "";

  const UrlRow = ({ id, label, help, url }) => (
    <div className="lms-portal-url-row">
      <label className="form-own-label">{label}</label>
      {help && <p className="text-muted small mb-1">{help}</p>}
      <div className="lms-portal-url-field">
        <input className="form-control" value={url} readOnly onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="btn secondary-btn lms-portal-copy-btn"
          onClick={() => copyToClipboard(id, url)}
        >
          <i className={`fas ${copiedKey === id ? "fa-check" : "fa-copy"} me-1`}></i>
          {copiedKey === id ? t("common.copied", "Copied") : t("common.copy", "Copy")}
        </button>
        <a className="btn lms-portal-open-btn" href={url} target="_blank" rel="noopener noreferrer">
          <i className="fas fa-external-link-alt me-1"></i>
          {t("admin.lms.settings.openLink", "Open")}
        </a>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{t("admin.lms.settings.title", "LMS Settings")} - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      {allowView && (
        <div className="admin-portal">
          <Header roleType="admin" />
          <div className={`${navbarToggle ? "active" : ""} wrapper`}>
            <SidebarComponent
              navbarToggle={navbarToggle}
              roleType="admin"
              menu="lms"
              subMenu="lms-settings"
            />
            <div className="main">
              <div className="main-wrapper">
                <div>
                  <p className="admin-banner">{t("admin.lms.settings.title", "LMS Settings")}</p>
                </div>
                <SidebarArrow handleSidebarToggle={setNavbarToggle} />

                <div className="container-fluid pt-3 lms-settings-page">
                  <div className="lms-settings-heading">
                    <div>
                      <h1>{t("admin.lms.settings.title", "LMS Settings")}</h1>
                      <p>Manage learner access, regional defaults, support details, and portal setup.</p>
                    </div>
                  </div>

                  <div className="lms-settings-tabs" role="tablist" aria-label="LMS settings sections">
                    {SETTINGS_TABS.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === item.key}
                        className={activeTab === item.key ? "active" : ""}
                        onClick={() => selectTab(item.key)}
                      >
                        <i className={`fas ${item.icon}`}></i>
                        <span>{item.label}</span>
                        {item.key === "region" && <strong>{districts.length}</strong>}
                      </button>
                    ))}
                  </div>

                  {activeTab === "access" && (
                    <section className="lms-settings-panel" role="tabpanel">
                      <div className="lms-settings-panel-body">
                        <h3 className="lms-section-title">
                          {t("admin.lms.settings.domainsTitle", "Approved Email Domains")}
                        </h3>
                        <p className="text-muted">
                          {t(
                            "admin.lms.settings.domainsHelp",
                            "Learners registering with an email on these domains are auto-approved. Everyone else is queued for your review on the Registrations page."
                          )}
                        </p>

                        <label htmlFor="lmsDomainInput" className="form-own-label">
                          {t("admin.lms.settings.domainsLabel", "Approved Email Domains")}
                        </label>
                        <div
                          className="lms-chip-input form-control admin"
                          onClick={() => inputRef.current?.focus()}
                        >
                          {domains.map((d) => (
                            <span className="lms-chip" key={d}>
                              {d}
                              <button
                                type="button"
                                className="lms-chip-x"
                                aria-label={t("common.delete", "Remove")}
                                onClick={() => removeDomain(d)}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </span>
                          ))}
                          <input
                            id="lmsDomainInput"
                            ref={inputRef}
                            className="lms-chip-field"
                            value={domainInput}
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={
                              domains.length
                                ? ""
                                : t("admin.lms.settings.domainsPlaceholder", "essex.gov.uk, essex.sch.uk")
                            }
                            onChange={(e) => setDomainInput(e.target.value.toLowerCase())}
                            onKeyDown={onDomainKey}
                            onBlur={() => domainInput.trim() && commitDomain()}
                          />
                        </div>
                        <small className="text-muted d-block">
                          {t("admin.lms.settings.domainsHint", "Press Enter or comma to add.")}
                        </small>
                        <small className="d-block mt-1 text-danger">
                          <i className="fas fa-info-circle me-1"></i>
                          Note: Do not start with @ (e.g. gmail.com)
                        </small>

                        <div className="lms-settings-actions">
                          <button
                            type="button"
                            className="btn secondary-btn lms-settings-save"
                            disabled={saving || !dirty}
                            onClick={save}
                          >
                            {saving ? (
                              <i className="fas fa-circle-notch fa-spin"></i>
                            ) : (
                              t("common.save", "Save")
                            )}
                          </button>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── Default Region ── */}
                  {activeTab === "region" && (
                    <section className="lms-settings-panel lms-settings-district-panel" role="tabpanel">
                      <div className="lms-settings-panel-body lms-region-manager-block">
                        <div className="lms-region-toolbar">
                          <div>
                            <h3 className="lms-section-title">
                              {t("admin.lms.settings.regionsTitle", "Manage Regions")}
                            </h3>
                            <p className="text-muted">
                              {t(
                                "admin.lms.settings.regionsHelp",
                                "Regions and districts shown here control learner registration dropdowns."
                              )}
                            </p>
                          </div>
                          <select
                            className="form-control lms-settings-filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                          >
                            <option value="all">{t("common.all", "All")}</option>
                            <option value="active">{t("common.active", "Active")}</option>
                            <option value="inactive">{t("admin.lms.settings.inactive", "Inactive")}</option>
                          </select>
                        </div>

                        <div className="lms-district-add lms-region-add">
                          <input
                            className="form-control"
                            placeholder={t("admin.lms.settings.regionName", "Region name")}
                            value={newRegion.name}
                            onChange={(e) => setNewRegion((r) => ({ ...r, name: e.target.value }))}
                          />
                          <button
                            type="button"
                            className="btn secondary-btn"
                            disabled={savingRegionConfig}
                            onClick={saveRegionConfig}
                          >
                            {savingRegionConfig ? (
                              <i className="fas fa-circle-notch fa-spin"></i>
                            ) : (
                              <>
                                <i className="fas fa-plus me-2"></i>
                                {t("admin.lms.settings.addRegion", "Add region")}
                              </>
                            )}
                          </button>
                        </div>

                        <div className="table-own table-responsive mt-3">
                          <table className="table table-hover lms-learner-table">
                            <thead className="table-head">
                              <tr>
                                <th>{t("common.name", "Name")}</th>
                                <th style={{ width: 150 }}>{t("admin.lms.settings.districtsCol", "Districts")}</th>
                                <th style={{ width: 110 }}>{t("common.status", "Status")}</th>
                                <th style={{ width: 300 }}>{t("common.actions", "Actions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {regionRows.length ? (
                                regionRows.map((r) => {
                                  const editing = editingRegionId === r._id;
                                  const dCount = districtsOfRegion(r._id).length;
                                  return (
                                    <tr key={r._id} className={!isEnabled(r) ? "lms-row-muted" : ""}>
                                      <td>
                                        {editing ? (
                                          <input
                                            className="form-control"
                                            value={editingRegion.name}
                                            onChange={(e) => setEditingRegion((v) => ({ ...v, name: e.target.value }))}
                                          />
                                        ) : (
                                          r?.name || "-"
                                        )}
                                      </td>
                                      <td>
                                        <button
                                          type="button"
                                          className="lms-district-count-btn"
                                          onClick={() => openDistrictModal(r)}
                                          title={t("admin.lms.settings.viewDistricts", "View districts")}
                                        >
                                          <i className="fas fa-city"></i>
                                          {t("admin.lms.settings.districtCount", "{{count}} districts", { count: dCount })}
                                        </button>
                                      </td>
                                      <td>
                                        <span className={`lms-status-pill ${isEnabled(r) ? "active" : "inactive"}`}>
                                          {isEnabled(r) ? t("common.active", "Active") : t("admin.lms.settings.inactive", "Inactive")}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="lms-settings-row-actions">
                                          {editing ? (
                                            <>
                                              <button type="button" className="btn btn-sm lms-icon-btn save" onClick={() => updateRegion(r)}>
                                                <i className="fas fa-check"></i>
                                              </button>
                                              <button type="button" className="btn btn-sm lms-icon-btn" onClick={cancelEditRegion}>
                                                <i className="fas fa-times"></i>
                                              </button>
                                            </>
                                          ) : (
                                            <>
                                              <button
                                                type="button"
                                                className="btn btn-sm secondary-btn lms-view-districts-btn"
                                                onClick={() => openDistrictModal(r)}
                                              >
                                                <i className="fas fa-city me-1"></i>
                                                {t("admin.lms.settings.viewDistricts", "View districts")}
                                              </button>
                                              <button type="button" className="btn btn-sm lms-icon-btn" onClick={() => beginEditRegion(r)} title={t("common.edit", "Edit")}>
                                                <i className="fas fa-edit"></i>
                                              </button>
                                              <button
                                                type="button"
                                                className={`btn btn-sm lms-toggle-btn ${isEnabled(r) ? "deactivate" : "activate"}`}
                                                onClick={() => toggleRegion(r)}
                                              >
                                                {isEnabled(r)
                                                  ? t("common.deactivate", "Deactivate")
                                                  : t("common.activate", "Activate")}
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={4} className="text-center text-muted">
                                    {t("admin.lms.settings.noRegions", "No regions found.")}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                    </section>
                  )}

                  {activeTab === "support" && (
                    <section className="lms-settings-panel" role="tabpanel">
                      <div className="lms-settings-panel-body lms-support-block">
                        <h3 className="lms-section-title">
                          {t("admin.lms.settings.supportTitle", "Help & support")}
                        </h3>
                        <p className="text-muted">
                          {t(
                            "admin.lms.settings.supportHelp",
                            "Set the contact details and support guidance shown to learners across the LMS portal."
                          )}
                        </p>


                        <div className="lms-support-grid">
                          <div className="lms-support-field">
                            <label htmlFor="lmsSupportEmail" className="form-own-label">
                              {t("admin.lms.settings.supportEmail", "Support email")}
                            </label>
                            <input
                              id="lmsSupportEmail"
                              type="email"
                              inputMode="email"
                              className="form-control"
                              placeholder={t("admin.lms.settings.supportEmailPlaceholder", "support@yourorganisation.org")}
                              value={supportForm.email}
                              onChange={(e) => setSupportForm((current) => ({ ...current, email: e.target.value }))}
                            />
                          </div>

                          <div className="lms-support-field">
                            <label htmlFor="lmsSupportPhone" className="form-own-label">
                              {t("admin.lms.settings.supportPhone", "Support phone")}
                            </label>
                            <input
                              id="lmsSupportPhone"
                              type="tel"
                              className="form-control"
                              placeholder={t("admin.lms.settings.supportPhonePlaceholder", "+44 1234 567890")}
                              value={supportForm.contactNumber}
                              onChange={(e) => setSupportForm((current) => ({ ...current, contactNumber: e.target.value }))}
                            />
                          </div>

                          <div className="lms-support-field lms-support-field-full">
                            <label htmlFor="lmsSupportContent" className="form-own-label">
                              {t("admin.lms.settings.supportContent", "Help & support content")}
                            </label>
                            <textarea
                              id="lmsSupportContent"
                              rows={6}
                              className="form-control lms-support-textarea"
                              placeholder={t(
                                "admin.lms.settings.supportContentPlaceholder",
                                "Add the guidance learners should see when they need help or support."
                              )}
                              value={supportForm.helpAndSupport}
                              onChange={(e) => setSupportForm((current) => ({ ...current, helpAndSupport: e.target.value }))}
                            />
                          </div>

                        </div>

                        <div className="lms-settings-actions">
                          <button
                            type="button"
                            className="btn secondary-btn lms-settings-save"
                            disabled={savingSupport || !supportDirty}
                            onClick={saveSupport}
                          >
                            {savingSupport ? (
                              <i className="fas fa-circle-notch fa-spin"></i>
                            ) : (
                              t("common.save", "Save")
                            )}
                          </button>
                        </div>
                      </div>
                    </section>
                  )}

                  {activeTab === "portal" && (
                    <section className="lms-settings-panel" role="tabpanel">
                      <div className="lms-settings-panel-body lms-portal-url-block">
                        <h3 className="lms-section-title">
                          {t("admin.lms.settings.portalTitle", "Learner portal URL")}
                        </h3>
                        {/* <p className="text-muted">
                          {t(
                            "admin.lms.settings.portalHelp",
                            "Your learners access the portal at your organisation's own web address, built from your short name."
                          )}
                        </p> */}

                        {shortName ? (
                          <>
                            {/* <div className="lms-portal-summary">
                              <div className="lms-portal-summary-item">
                                <span className="lms-portal-summary-label">
                                  {t("admin.lms.settings.shortNameLabel", "Organisation short name")}
                                </span>
                                <span className="lms-portal-shortname">{shortName}</span>
                              </div>
                              <div className="lms-portal-summary-item">
                                <span className="lms-portal-summary-label">
                                  {t("admin.lms.settings.learnerDomain", "Learner domain")}
                                </span>
                                <span className="lms-portal-domain">{LEARNER_DOMAIN}</span>
                              </div>
                              <div className="lms-portal-summary-item">
                                <span className="lms-portal-summary-label">
                                  {t("common.status", "Status")}
                                </span>
                                <span className={`lms-status-pill ${lmsEnabled ? "active" : "inactive"}`}>
                                  {lmsEnabled
                                    ? t("admin.lms.settings.portalEnabled", "Enabled")
                                    : t("admin.lms.settings.portalDisabled", "Disabled")}
                                </span>
                              </div>
                            </div> */}

                            <UrlRow
                              id="register"
                              label={t("admin.lms.settings.portalRegisterUrl", "Learner registration URL")}
                              help={t("admin.lms.settings.portalRegisterHelp", "Share this so new learners can create an account.")}
                              url={portalRegisterUrl}
                            />

                            <UrlRow
                              id="login"
                              label={t("admin.lms.settings.portalLoginUrl", "Learner sign-in URL")}
                              help={t("admin.lms.settings.portalLoginHelp", "Share this with learners who already have an account.")}
                              url={portalLoginUrl}
                            />

                          </>
                        ) : (
                          <div className="lms-portal-note warning">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {t(
                              "admin.lms.settings.noShortName",
                              "No learner short name is configured for your organisation yet. Please contact the platform team to set one up."
                            )}
                          </div>
                        )}
                        <div className="lms-portal-note">
                          <i className="fas fa-info-circle me-2"></i>
                          {t(
                            "admin.lms.settings.shortNameManaged",
                            "The short name is set by the platform team. To change your web address, contact support — existing learner links will stop working."
                          )}
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage districts modal (per region) ── */}
      {districtModalRegion && (
        <div
          className={`modal fade show lms-district-modal${confirmDeleteDistrict ? " lms-district-modal--inactive" : ""}`}
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lms-district-modal-title"
        >
          <div className="modal-dialog modal-dialog-centered lms-district-modal-dialog">
            <div className="modal-content">
              <div className="modal-header lms-district-modal-header">
                <div>
                  <h3 id="lms-district-modal-title">
                    {t("admin.lms.settings.districtsForRegion", "Districts")} · {districtModalRegion?.name}
                  </h3>
                  <span className="text-muted">
                    {t("admin.lms.settings.districtCount", "{{count}} districts", {
                      count: modalRegionDistricts.length,
                    })}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn lms-modal-close"
                  onClick={closeDistrictModal}
                  aria-label={t("common.close", "Close")}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body lms-district-modal-body">
                <p className="text-muted small mb-3">
                  {t(
                    "admin.lms.settings.districtsHelp",
                    "Districts appear in the learner registration form, grouped under their region."
                  )}
                </p>

                {/* Add district (to this region) */}
                <div className="lms-district-add">
                  <input
                    className="form-control"
                    placeholder={t("admin.lms.settings.districtName", "District name")}
                    value={newDistrict.name}
                    onChange={(e) => setNewDistrict((d) => ({ ...d, name: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && addDistrict()}
                  />
                  <button
                    type="button"
                    className="btn secondary-btn"
                    disabled={addingDistrict}
                    onClick={addDistrict}
                  >
                    {addingDistrict ? (
                      <i className="fas fa-circle-notch fa-spin"></i>
                    ) : (
                      <>
                        <i className="fas fa-plus me-2"></i>
                        {t("admin.lms.settings.addDistrict", "Add district")}
                      </>
                    )}
                  </button>
                </div>

                <div className="table-own table-responsive mt-3 lms-district-modal-table">
                  <table className="table table-hover lms-district-table">
                    <thead className="table-head">
                      <tr>
                        <th>{t("common.name", "Name")}</th>
                        <th style={{ width: 110 }}>{t("common.status", "Status")}</th>
                        <th style={{ width: 150 }}>{t("common.actions", "Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalRegionDistricts.length ? (
                        modalRegionDistricts.map((d) => {
                          const editing = editingDistrictId === d._id;
                          return (
                            <tr key={d._id} className={!isEnabled(d) ? "lms-row-muted" : ""}>
                              <td>
                                {editing ? (
                                  <input
                                    className="form-control"
                                    value={editingDistrict.name}
                                    onChange={(e) => setEditingDistrict((v) => ({ ...v, name: e.target.value }))}
                                  />
                                ) : (
                                  d?.name || "-"
                                )}
                              </td>
                              <td>
                                <span className={`lms-status-pill ${isEnabled(d) ? "active" : "inactive"}`}>
                                  {isEnabled(d) ? t("common.active", "Active") : t("admin.lms.settings.inactive", "Inactive")}
                                </span>
                              </td>
                              <td>
                                <div className="lms-settings-row-actions">
                                  {editing ? (
                                    <>
                                      <button type="button" className="btn btn-sm lms-icon-btn save" onClick={() => updateDistrict(d)} title={t("common.save", "Save")}>
                                        <i className="fas fa-check"></i>
                                      </button>
                                      <button type="button" className="btn btn-sm lms-icon-btn" onClick={cancelEditDistrict} title={t("common.cancel", "Cancel")}>
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button type="button" className="btn btn-sm lms-icon-btn" onClick={() => beginEditDistrict(d)} title={t("common.edit", "Edit")}>
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button type="button" className="btn btn-sm lms-icon-btn text-danger" onClick={() => setConfirmDeleteDistrict(d)} title={t("common.delete", "Delete")}>
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            {t("admin.lms.settings.noDistricts", "No districts yet — add one above.")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer lms-district-modal-footer">
                <button type="button" className="btn secondary-btn btn-own-grey" onClick={closeDistrictModal}>
                  {t("common.close", "Close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteDistrict && (
        <div className="lms-district-confirm" role="dialog" aria-modal="true" aria-labelledby="lms-district-confirm-title">
          <button
            type="button"
            className="lms-district-confirm__backdrop"
            onClick={() => deleteDistrict(null)}
            aria-label={t("common.close", "Close")}
          />
          <div className="lms-district-confirm__dialog">
            <div className="lms-district-confirm__content">
              <h3 id="lms-district-confirm-title" className="lms-district-confirm__title">
                {t("admin.lms.settings.confirmDeleteDistrictTitle", "Delete this district?")}
              </h3>
              <p className="lms-district-confirm__meta">
                {t("admin.lms.settings.districtName", "District")} : {confirmDeleteDistrict?.name}
              </p>
              <p className="lms-district-confirm__description">
                {t(
                  "admin.lms.settings.confirmDeleteDistrictHelp",
                  "This district will be removed from the learner registration form."
                )}
              </p>
              <div className="lms-district-confirm__actions">
                <button
                  type="button"
                  onClick={() => deleteDistrict(null)}
                  className="btn secondary-btn btn-own btn-own-grey min-height-btn min-width-btn-md"
                >
                  {t("common.confirmModal.no", "No")}
                </button>
                <button
                  type="button"
                  onClick={() => deleteDistrict(true)}
                  className="btn primary-btn btn-own btn-own-primary min-height-btn min-width-btn-md"
                >
                  {t("common.confirmModal.yes", "Yes")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(LmsSettings);
